import { Box, CircularProgress, Typography } from '@material-ui/core';
import { Layout, LoginLogoImg, useBaseStore } from '@roc/feature-app-core';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import { useSecureLinkSession } from '@roc-capital/secure-link/react';
import { Button } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useSecureLinkAchFormStore } from '../hooks/useSecureLinkAchFormStore';
import { isPlaidOAuthReturn } from '../stores/secureLinkAchFormStore';
import { ACHFormPageComponent } from './achFormPage';

const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

/**
 * The ACH consent form for secure links: /link/ach. The borrower arrives here from the platform landing page
 * (/platform-app/link#token), which started the session; every call is authorized by that session, and the loan and
 * borrower come from it on the server. Replaces /ach/:token, which carried an HMAC token in the URL.
 */
export const SecureLinkACHFormPage = observer(() => {
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry, secondsLeft } = useSecureLinkSession(client, { purpose: 'ACH_FORM' });
  const { achFormStore } = useSecureLinkAchFormStore();
  const oauthReturn = isPlaidOAuthReturn();

  // A layout effect, so it runs before the ACH screens' own effects (the first data fetch) on the same commit.
  useLayoutEffect(() => achFormStore.setRequest(request), [achFormStore, request]);

  // The bank step warns before opening Plaid when the session is about to end.
  useEffect(() => achFormStore.setSecondsLeft(secondsLeft), [achFormStore, secondsLeft]);

  return (
    <Layout maxWidth={'lg'}>
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <LoginLogoImg imgStyle={{ width: '150px' }} />
      </Box>
      {status === 'loading' && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {status === 'ended' && (
        <Message
          title="Your session has ended"
          text={
            oauthReturn
              ? 'If you were sent here from your bank, please return to the browser tab where you started, or open the link from your email again to continue.'
              : 'For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link from your email again to continue.'
          }
        />
      )}
      {status === 'unavailable' && (
        <Message
          title="We couldn't open your form"
          text="This is usually temporary. Check your connection and try again."
          action={{ label: 'Try again', onClick: retry }}
        />
      )}
      {status === 'active' && (
        <ACHFormPageComponent encodedInput="secure-link-session" />
      )}
    </Layout>
  );
});

/**
 * /ach/:token, the old ACH link format (an HMAC token in the URL). Those links no longer work: the lender sends a new
 * one, which opens /link/ach.
 */
export const AchLinkReplacedPage = () => (
  <Layout maxWidth={'lg'}>
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <LoginLogoImg imgStyle={{ width: '150px' }} />
    </Box>
    <Message
      title="This link is no longer valid"
      text="Please ask your lender to send you a new link to complete your ACH form."
    />
  </Layout>
);

/**
 * /liquidity-verification/:token, the old public Plaid liquidity verification link. Its backend endpoints are gone, so
 * the page cannot work; nportal still emails the link, so it lands here instead of a 404.
 */
export const LiquidityVerificationLinkReplacedPage = () => (
  <Layout maxWidth={'lg'}>
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <LoginLogoImg imgStyle={{ width: '150px' }} />
    </Box>
    <Message
      title="This link is no longer valid"
      text="Please contact your lender to complete your liquidity verification."
    />
  </Layout>
);
